/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "./CalendarPaymentScreen.css";
import backIcon from "../../assets/images/back-icon.svg";
import paymentRightArrow from "../../assets/images/membership-payment-right-arrow.svg";
import letsDoItIcon from "../../assets/images/lets-do-it-icon.svg";
import eventCardIcon from "../../assets/images/calendar-event-card.png";
import mbStudioCardImage from "../../assets/images/event-card-images/mb-studio-card-image.png";
import eventCardImage from "../../assets/images/event-card-images/event-card-image.png";
import workspaceCardImage from "../../assets/images/event-card-images/workspace-card-image.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { CircularProgress } from "@mui/material";
import {
  CALENDAR_RESERVATION_TABS,
  CALENDAR_RESERVATION_TYPE,
  PAYMENT_STATUS,
  PROFILE_STATUS,
  PURCHASE_TYPE,
} from "utils/constants";
import { MESSAGE, STORAGE_KEY } from "utils/strings";
import ROUTE_PATHS from "utils/routePaths";
import {
  formatDateString,
  formatRetreatTime,
  formatWorkspaceTimeRange,
} from "utils/utils";
import { useStorage } from "services/StorageProvider";
import {
  trackCheckoutStarted,
  trackPaymentInitiated,
  trackPaymentSuccess,
  trackPaymentFailed,
  trackCheckoutAbandoned,
  trackBackClicked,
} from "utils/analytics";

const CalendarPaymentScreen: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfileData } = useStorage();
  const [selectedSlotData, setSelectedSlotData] = useState<any>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string>("");
  const [isActiveMember, setIsActiveMember] = useState<boolean>(false);
  const [selectedSlotPrice, setSelectedSlotPrice] = useState<number | null>(
    null,
  );
  const [selectedSlotDiscountedPrice, setSelectedSlotDiscountedPrice] =
    useState<number | null>(null);
  const { createPayment, verifyPayment } = useWSMApi();

  const purchaseMembershipMutation = useMutation({
    mutationFn: (data: any) => createPayment(data),
    onSuccess: (response, obj) => {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: obj.product.total_price,
        currency: obj.currency,
        name: "The Foundry", //Name Shown in Razorpay Form
        description: obj.product.description,
        order_id: response.id, // This is the order_id created in the backend
        prefill: {
          name: obj.customer.name,
          email: obj.customer.email,
          contact: obj.customer.contact,
        },
        theme: {
          color: "#1C1C1E",
        },
        handler: async function (paymentResponse: any) {
          setPaymentId(paymentResponse.razorpay_payment_id);
          trackPaymentSuccess(
            obj.product.total_price / 100,
            paymentResponse.razorpay_payment_id,
            obj.product.description,
            "slot",
          );
          verifyPaymentMutation.mutate({
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => {
            if (!paymentStatus) {
              setPaymentStatus(PAYMENT_STATUS.CANCELLED);
              trackCheckoutAbandoned("payment");
            }
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    },
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: (data: any) => verifyPayment(data),
    onSuccess: (response) => {
      setPaymentStatus(response.status);
    },
    onError: () => {
      setPaymentStatus(PAYMENT_STATUS.FAILED);
      trackPaymentFailed("payment_verification_failed");
    },
  });

  useEffect(() => {
    setIsActiveMember(
      userProfileData?.credit?.status === PROFILE_STATUS.MEMBER,
    );
  }, []);

  useEffect(() => {
    const storedSlotData = localStorage.getItem(STORAGE_KEY.SELECTED_SLOT_DATA);
    if (storedSlotData) {
      const slotData = JSON.parse(storedSlotData);
      setSelectedSlotData(slotData);
      const price = isActiveMember
        ? slotData?.member_slot_price
        : slotData?.non_member_slot_price;
      setSelectedSlotPrice(price);
      setSelectedSlotDiscountedPrice(
        isActiveMember
          ? slotData?.member_discounted_slot_price
          : slotData?.non_member_discounted_slot_price,
      );
      trackCheckoutStarted(Number(price ?? 0));
    }
  }, [isActiveMember]);

  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      const paymentData = {
        title: getPaymentTitle(),
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem(
        STORAGE_KEY.PAYMENT_SUCCESS_DATA,
        JSON.stringify(paymentData),
      );
      navigate(ROUTE_PATHS.WSM_ALL_SET, {
        state: { from: location.pathname },
        replace: true,
      });
    }
  }, [paymentStatus, paymentId, navigate]);

  const getPaymentTitle = () => {
    let title =
      CALENDAR_RESERVATION_TABS.find(
        (type) => type.value === selectedSlotData?.service_type,
      )?.name || "";
    if (title.endsWith("s")) {
      title = title.slice(0, -1);
    }
    return title;
  };

  const handleBackButtonClick = () => {
    trackBackClicked("CalendarPayment");
    navigate(-1);
  };

  const handleProceedToPaymentClick = () => {
    const amountInSubunits = Math.round(Number(getFinalSlotPrice()) * 100);
    const passedObj = {
      amount: getFinalSlotPrice(),
      currency: "INR",
      customer: {
        name: `${userProfileData?.first_name} ${userProfileData?.last_name}`,
        email: userProfileData?.email,
        contact: userProfileData?.mobile_number,
      },
      product: {
        total_price: amountInSubunits, // Amount in paise
        slot_id: selectedSlotData?.id,
        description: selectedSlotData?.title,
        price: getSlotPrice(),
        with_gst_price: getSlotPriceWithGST(),
        credit: selectedSlotData?.credits_required,
      },
    };
    setPaymentStatus(null);
    trackPaymentInitiated("razorpay");
    purchaseMembershipMutation.mutate(passedObj);
  };

  const getSlotPrice = () => {
    return selectedSlotDiscountedPrice
      ? selectedSlotDiscountedPrice
      : selectedSlotPrice;
  };

  const getSlotPriceWithGST = () => {
    const price = getSlotPrice();
    return Math.floor((Number(price) * selectedSlotData?.slot_gst) / 100);
  };

  const getFinalSlotPrice = () => {
    const price = selectedSlotDiscountedPrice
      ? selectedSlotDiscountedPrice
      : selectedSlotPrice;
    const gstPrice = Math.floor(
      (Number(price) * selectedSlotData?.slot_gst) / 100,
    );
    return Number(price) + gstPrice;
  };

  const getEventCardImage = () => {
    if (selectedSlotData?.service_type === CALENDAR_RESERVATION_TYPE.EVENTS) {
      return eventCardImage;
    } else if (
      selectedSlotData?.service_type === CALENDAR_RESERVATION_TYPE.DF_WORKSPACE
    ) {
      return workspaceCardImage;
    } else if (
      selectedSlotData?.service_type === CALENDAR_RESERVATION_TYPE.MB_STUDIO
    ) {
      return mbStudioCardImage;
    } else {
      return eventCardIcon;
    }
  };

  return (
    <div className="wms-mobile-container">
      {purchaseMembershipMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {purchaseMembershipMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {paymentStatus === PAYMENT_STATUS.FAILED ? (
        <SnackbarAlert type={"error"}>{MESSAGE.PAYMENT_FAILED}</SnackbarAlert>
      ) : (
        ""
      )}
      <div className="calendar-payment-detail-screen-container">
        <div className="calendar-payment-detail-header-container">
          <img
            src={backIcon}
            alt="dropdown-logo"
            className="image"
            onClick={handleBackButtonClick}
          />
          <div className="title">Payment Mode</div>
        </div>
        <div className="calendar-payment-detail-tab-bar-container">
          <div
            className="calendar-payment-detail-tab-bar-item-container"
            style={{ fontWeight: "700" }}
          >
            <div className="circle-container review">1</div>
            Review
          </div>
          <img src={paymentRightArrow} alt="payment-right-arrow" />
          <div className="calendar-payment-detail-tab-bar-item-container">
            <div className="circle-container payment">2</div>
            Payment
          </div>
        </div>
        <div className="calendar-payment-detail-card-container">
          <div className="image-text-container">
            <img
              className="card-image"
              src={getEventCardImage()}
              alt="membership-card"
            />
            <div className="custom-text-overlay"></div>
          </div>
          <div className="right-container">
            <div className="sub-container">
              <div className="plan-amount-container">
                {`INR ${getSlotPrice()?.toLocaleString("en-US")}`}
                <span>Only</span>
              </div>
              {selectedSlotDiscountedPrice && (
                <div className="plan-offer-price-container">
                  {`INR ${selectedSlotPrice?.toLocaleString("en-US")}`}
                </div>
              )}
            </div>
            <div className="total-credit-text">{selectedSlotData?.title}</div>
            <div className="sub-container">
              <div className="valid-up-to-text">
                {formatDateString(selectedSlotData?.start_time)}
              </div>
              <div className="credit-validity-container">
                {selectedSlotData?.service_type ===
                CALENDAR_RESERVATION_TYPE.RETREAT
                  ? formatRetreatTime(selectedSlotData?.start_time)
                  : formatWorkspaceTimeRange(
                      selectedSlotData?.start_time,
                      selectedSlotData?.end_time,
                    )}
              </div>
            </div>
          </div>
        </div>
        <div className="calendar-payment-summary-details-container">
          <div className="header-container">
            <span>
              {CALENDAR_RESERVATION_TABS.find(
                (type) => type.value === selectedSlotData?.service_type,
              )?.name || ""}
            </span>
          </div>
          <div className="divider" style={{ marginBottom: "20px" }}></div>
          <div className="payment-detail">
            <span>Sub Total</span>
            <span>{`INR ${getSlotPrice()?.toLocaleString("en-US")}`}</span>
          </div>
          <div className="payment-detail">
            <span>GST</span>
            <span>INR {getSlotPriceWithGST()?.toLocaleString("en-US")}</span>
          </div>
          <div className="divider" style={{ marginBottom: "8px" }}></div>
          <div className="payment-detail">
            <span className="total">Total</span>
            <span className="total">{`INR ${getFinalSlotPrice()?.toLocaleString(
              "en-US",
            )}`}</span>
          </div>
        </div>
      </div>
      <div
        className="calendar-payment-detail-purchase-btn"
        onClick={handleProceedToPaymentClick}
      >
        {purchaseMembershipMutation?.isPending ? (
          <CircularProgress size={30} color="inherit" />
        ) : (
          <>
            Proceed to payment
            <img src={letsDoItIcon} alt="lets-do-it-logo" />
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarPaymentScreen;
